import { useEffect, useMemo, useRef, useState } from "react";
import { FaCog, FaColumns, FaFileCsv, FaFileExcel, FaFilePdf, FaPrint, FaSlidersH } from "react-icons/fa";
import { AppButton } from "../../_CORE/APP/Templates/_UI/Button/_index";
import { AppPopover, PopoverItem, PopoverTitle, PopoverDivider } from "../../_CORE/APP/Templates/_UI/Popover/_index";
import { TablePerPage, PER_PAGE_OPTIONS, onExportCsv, onExportExcel, onExportPdf, onPrintTable } from "../../_CORE/APP/Templates/_UI/Table/_index";
import { FieldSelect, FieldDate } from "../../_CORE/APP/Templates/_UI/Form/_index";
import { DATE_RANGES } from "./useDateRange";
import { RecordExportPickerModal } from "./RecordExportPickerModal";
import { AdvancedFilterPanel } from "./AdvancedFilterPanel";
import "./_Styles/ViewToolbar.css";

/*
    Barra de herramientas canónica.
    - Los filtros viven a la izquierda sin superponerse.
    - El engranaje permite elegir columnas y recién después exportar.
    - No se ofrece copiar tabla al portapapeles: CSV/Excel/PDF/Imprimir cubren el caso.
*/
export const ViewToolbar = ({
    filters = null,
    exportColumns = null,
    exportRows = [],
    exportName = "registros",
    total = null,
    totalSingular = "registro",
    totalPlural = "registros",
    perPage = null,
    onChangePerPage = null,
    actions = null,
    dateRange = null,
    extraMenuItems = [],
    popoverClassName = "",
    menuButtonLabel = null,
    expandedContent = null,
}) => {
    const REF__useRefExport = useRef(null);
    const [useIsExportOpen, setIsExportOpen] = useState(false);
    const [useIsColumnPickerOpen, setColumnPickerOpen] = useState(false);
    const [useSelectedColumnKeys, setSelectedColumnKeys] = useState([]);
    const [useAutoAdvancedOpen, setAutoAdvancedOpen] = useState(false);
    const [useExactDate, setExactDate] = useState(null);

    const EXPORTABLE_COLUMNS = useMemo(() => (exportColumns ?? []).filter((column) => column?.isExportable !== false), [exportColumns]);

    useEffect(() => {
        const available = EXPORTABLE_COLUMNS.map((column) => column.key);
        setSelectedColumnKeys((current) => {
            const kept = current.filter((key) => available.includes(key));
            return kept.length ? kept : available;
        });
    }, [EXPORTABLE_COLUMNS]);

    const ACTIVE_EXPORT_COLUMNS = useMemo(() => {
        const selected = new Set(useSelectedColumnKeys);
        return EXPORTABLE_COLUMNS.filter((column) => selected.has(column.key));
    }, [EXPORTABLE_COLUMNS, useSelectedColumnKeys]);

    const HAS_EXPORT = EXPORTABLE_COLUMNS.length > 0 && exportRows.length > 0;
    const HAS_EXTRA_MENU = Array.isArray(extraMenuItems) && extraMenuItems.length > 0;
    const HAS_MENU = HAS_EXPORT || HAS_EXTRA_MENU;
    const HAS_META = total !== null || perPage !== null;

    const PER_PAGE_AVAILABLE = PER_PAGE_OPTIONS.map((option) => ({
        ...option,
        isDisabled: Number(option.value) !== -1 && total !== null && total < Number(option.value),
    }));

    const onRunExport = (action) => {
        setIsExportOpen(false);
        action();
    };

    const onOpenColumns = () => {
        setIsExportOpen(false);
        setColumnPickerOpen(true);
    };

    return (
        <div className="LStudio__app_toolbar">
            <div className="LStudio__app_toolbar__main">
                <div className="LStudio__app_toolbar__filters">
                    {filters}
                    {dateRange && !expandedContent && (
                        <AppButton variant={useAutoAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAutoAdvancedOpen((current) => !current)}>
                            {dateRange.useRange?.value ? "Avanzado (1)" : "Avanzado"}
                        </AppButton>
                    )}
                </div>

                <div className="LStudio__app_toolbar__actions">
                    {(exportColumns || HAS_EXTRA_MENU) && (
                        <>
                            <AppButton ref={REF__useRefExport} variant="outline" tone="neutral" icon={<FaCog />}
                                isIconOnly={!menuButtonLabel} isDisabled={!HAS_MENU} aria-label="Guardar registros"
                                onClick={() => setIsExportOpen(!useIsExportOpen)}>{menuButtonLabel}</AppButton>

                            <AppPopover isOpen={useIsExportOpen} onClose={() => setIsExportOpen(false)}
                                anchorRef={REF__useRefExport} align="end" role="menu" width={250} className={popoverClassName}>
                                <PopoverTitle>Guardar registros</PopoverTitle>

                                {HAS_EXPORT && (
                                    <>
                                        <PopoverItem icon={<FaColumns />} onClick={onOpenColumns}>
                                            Elegir columnas ({ACTIVE_EXPORT_COLUMNS.length}/{EXPORTABLE_COLUMNS.length})
                                        </PopoverItem>
                                        <PopoverDivider />
                                        <PopoverItem icon={<FaFileCsv />} isDisabled={ACTIVE_EXPORT_COLUMNS.length === 0}
                                            onClick={() => onRunExport(() => onExportCsv(ACTIVE_EXPORT_COLUMNS, exportRows, `${exportName}.csv`))}>
                                            Descargar CSV
                                        </PopoverItem>
                                        <PopoverItem icon={<FaFileExcel />} isDisabled={ACTIVE_EXPORT_COLUMNS.length === 0}
                                            onClick={() => onRunExport(() => onExportExcel(ACTIVE_EXPORT_COLUMNS, exportRows, `${exportName}.xlsx`, "Datos"))}>
                                            Descargar Excel
                                        </PopoverItem>
                                        <PopoverItem icon={<FaFilePdf />} isDisabled={ACTIVE_EXPORT_COLUMNS.length === 0}
                                            onClick={() => onRunExport(() => onExportPdf(ACTIVE_EXPORT_COLUMNS, exportRows, `${exportName}.pdf`, exportName))}>
                                            Descargar PDF
                                        </PopoverItem>
                                        <PopoverDivider />
                                        <PopoverItem icon={<FaPrint />} isDisabled={ACTIVE_EXPORT_COLUMNS.length === 0}
                                            onClick={() => onRunExport(() => onPrintTable(ACTIVE_EXPORT_COLUMNS, exportRows, exportName))}>
                                            Imprimir
                                        </PopoverItem>
                                    </>
                                )}

                                {HAS_EXPORT && HAS_EXTRA_MENU && <PopoverDivider />}
                                {HAS_EXTRA_MENU && extraMenuItems.map((item, index) => item?.divider
                                    ? <PopoverDivider key={`extra_divider_${index}`} />
                                    : <PopoverItem key={item?.key ?? `${item?.label ?? "item"}_${index}`}
                                        icon={item?.icon ?? null} tone={item?.tone ?? "neutral"}
                                        isDisabled={!!item?.isDisabled}
                                        onClick={() => onRunExport(() => item?.onClick?.())}>
                                        {item?.label}
                                    </PopoverItem>)}
                            </AppPopover>
                        </>
                    )}
                    {actions}
                </div>
            </div>

            {(expandedContent || (dateRange && !expandedContent && useAutoAdvancedOpen)) && (
                <div className="LStudio__app_toolbar__expanded">
                    {expandedContent || <AdvancedFilterPanel isOpen={useAutoAdvancedOpen} activeCount={dateRange.useRange?.value ? 1 : 0} onClear={() => { dateRange.onChangeValue(""); setExactDate(null); }}>
                        <FieldSelect id="date_range" label="Periodo" options={DATE_RANGES}
                            value={dateRange.useRange.value}
                            onChange={(event) => { setExactDate(null); dateRange.onChangeValue(event.target.value); }} />
                        <FieldDate id="rango_desde" label="Desde" placeholder="Desde"
                            value={dateRange.useRange.from} onChange={(date) => { setExactDate(null); if (dateRange.useRange.value !== "custom") dateRange.onChangeValue("custom"); dateRange.onChangeFrom(date); }} isClearable />
                        <FieldDate id="rango_hasta" label="Hasta" placeholder="Hasta"
                            value={dateRange.useRange.to} onChange={(date) => { setExactDate(null); if (dateRange.useRange.value !== "custom") dateRange.onChangeValue("custom"); dateRange.onChangeTo(date); }} isClearable />
                        <FieldDate id="fecha_exacta" label="Fecha exacta" value={useExactDate} onChange={(date) => {
                            setExactDate(date || null);
                            if (!date) return;
                            dateRange.onChangeValue("custom");
                            requestAnimationFrame(() => { dateRange.onChangeFrom(date); dateRange.onChangeTo(date); });
                        }} isClearable />
                    </AdvancedFilterPanel>}
                </div>
            )}

            {HAS_META && (
                <div className="LStudio__app_toolbar__meta">
                    {total !== null && <span className="LStudio__app_toolbar__total">{total} {total === 1 ? totalSingular : totalPlural}</span>}
                    {perPage !== null && <TablePerPage value={perPage} onChange={onChangePerPage}
                        options={PER_PAGE_AVAILABLE} totalItems={total ?? 0} label="Ver" />}
                </div>
            )}

            <RecordExportPickerModal isOpen={useIsColumnPickerOpen}
                columns={EXPORTABLE_COLUMNS} selectedKeys={useSelectedColumnKeys}
                onClose={() => setColumnPickerOpen(false)}
                onApply={(keys) => { setSelectedColumnKeys(keys); setColumnPickerOpen(false); }} />
        </div>
    );
};

export const onPaginate = (items, perPage) => (
    !perPage || Number(perPage) === -1 ? items : items.slice(0, Number(perPage))
);
